import React, { useMemo, useState } from "react";
import { Button, Card, Empty, Form, Input, List, Modal, Space, Tag, Typography } from "antd";
import { formatDateTime } from "../../../../utils/dateUtils";
import { HISTORY_TRACK_TYPE, getTrackFlightCount } from "../lib/trackUtils";

const { Paragraph, Text } = Typography;

const ScheduleDraftTrackPanel = ({
    title = "历史快照",
    tracks = [],
    currentTrackId,
    expandedTrackIds = [],
    onToggleTrack,
    onSaveTrackAsHistory,
    onRestoreTrack,
    onDeleteTrack,
    canManageTrack,
    canDeleteTrack,
    savingTrackAsHistory,
    restoringTrackId,
    restoringTrack,
    deletingTrack,
    deletingTrackId,
    showReferenceToggle = false,
}) => {
    const [saveOpen, setSaveOpen] = useState(false);
    const [form] = Form.useForm();
    const historyTracks = useMemo(
        () => tracks.filter((track) => track?.track_type === HISTORY_TRACK_TYPE).sort((left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()),
        [tracks],
    );

    const handleSubmitSave = async () => {
        try {
            const values = await form.validateFields();
            await onSaveTrackAsHistory?.(currentTrackId, values);
            form.resetFields();
            setSaveOpen(false);
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
        }
    };

    const handleRestore = (track) => {
        Modal.confirm({
            title: `确认恢复 ${track.name || "历史快照"}`,
            content: "系统会从此历史快照克隆航班并创建一条新的可编辑轨，原历史快照不会被删除。",
            okText: "从此快照恢复",
            cancelText: "取消",
            onOk: () => onRestoreTrack?.(track.id),
        });
    };

    const handleDelete = (track) => {
        Modal.confirm({
            title: `确认删除 ${track.name || track.track_type || "轨道"}`,
            content: "将删除整条轨道及其下所有航班，此操作无法恢复。",
            okText: "确认删除",
            okType: "danger",
            cancelText: "取消",
            onOk: () => onDeleteTrack?.(track.id),
        });
    };

    return (
        <>
            <Card
                title={title}
                className="schedule-draft-side-card"
                size="small"
                extra={
                    <Button type="primary" size="small" onClick={() => setSaveOpen(true)} disabled={!canManageTrack || !currentTrackId} loading={savingTrackAsHistory}>
                        保存当前轨
                    </Button>
                }
            >
                {historyTracks.length ? (
                    <List
                        dataSource={historyTracks}
                        renderItem={(track) => {
                            const expanded = expandedTrackIds.includes(String(track.id));
                            const metrics = track.metrics || {};

                            const actions = [];

                            if (showReferenceToggle && onToggleTrack) {
                                actions.push(
                                    <Button key="toggle" size="small" type={expanded ? "default" : "primary"} ghost={!expanded} onClick={() => onToggleTrack(track.id)}>
                                        {expanded ? "移除参考轨" : "挂载参考轨"}
                                    </Button>,
                                );
                            }

                            actions.push(
                                <Button key="restore" size="small" danger onClick={() => handleRestore(track)} disabled={!canManageTrack} loading={restoringTrack && String(restoringTrackId) === String(track.id)}>
                                    从此快照恢复
                                </Button>,
                                <Button key="delete" size="small" danger onClick={() => handleDelete(track)} disabled={!canDeleteTrack || String(track.id) === String(currentTrackId)} loading={deletingTrack && String(deletingTrackId) === String(track.id)}>
                                    删除
                                </Button>,
                            );

                            return (
                                <List.Item
                                    actions={actions}
                                >
                                    <div className="schedule-draft-track-item">
                                        <Space wrap size={[6, 6]}>
                                            <Tag color="blue">HISTORY</Tag>
                                            {track.source_type ? <Tag>{track.source_type}</Tag> : null}
                                            {expanded ? <Tag color="cyan">参考轨已挂载</Tag> : null}
                                        </Space>
                                        <Paragraph className="schedule-draft-track-summary">{track.summary || track.name || "暂无历史快照摘要"}</Paragraph>
                                        <Text type="secondary">创建人：{track.created_by || "未知"}</Text>
                                        <Text type="secondary">创建时间：{formatDateTime(track.created_at) || "--"}</Text>
                                        <Text type="secondary">
                                            快照：{metrics.flight_count ?? getTrackFlightCount(track)} 个航班 / {metrics.aircraft_count || 0} 架飞机
                                        </Text>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <Empty description="暂无历史快照轨" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Card>

            <Modal
                title="保存历史快照"
                open={saveOpen}
                onCancel={() => {
                    form.resetFields();
                    setSaveOpen(false);
                }}
                onOk={handleSubmitSave}
                okText="保存当前轨"
                cancelText="取消"
                confirmLoading={savingTrackAsHistory}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="快照名称" rules={[{ required: true, message: "请填写历史快照名称" }]}>
                        <Input maxLength={80} placeholder="例如：5月第一版快照" />
                    </Form.Item>
                    <Form.Item name="summary" label="快照摘要">
                        <Input.TextArea rows={4} maxLength={300} placeholder="例如：确认华北周计划的初版排班窗口，并锁定两段关键航班时刻。" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ScheduleDraftTrackPanel;
